import React from 'react';
import { motion } from 'framer-motion';
import { FaBuilding, FaImage } from 'react-icons/fa';

interface BasicInfoTabProps {
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  companyTaxNumber: string;
  companyLogo?: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  onChange: (field: string, value: string) => void;
  onLogoChange: (file: File) => void;
  t?: Record<string, string>;
}

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  companyName,
  companyAddress,
  companyPhone,
  companyEmail,
  companyTaxNumber,
  companyLogo,
  invoiceNumber,
  invoiceDate,
  dueDate,
  currency,
  onChange,
  onLogoChange,
  t
}) => {
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onLogoChange(file);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* معلومات الشركة */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <FaBuilding className="text-blue-600" />
          {t?.companyInfo || 'معلومات الشركة'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.companyName || 'اسم الشركة'}
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => onChange('companyName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.companyNamePlaceholder || 'أدخل اسم الشركة'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.companyEmail || 'البريد الإلكتروني'}
            </label>
            <input
              type="email"
              value={companyEmail}
              onChange={(e) => onChange('companyEmail', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.companyEmailPlaceholder || 'company@example.com'}
            />
          </div>

          {/* الرقم الضريبي */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.companyTaxNumber || 'الرقم الضريبي'}
            </label>
            <input
              type="text"
              value={companyTaxNumber}
              onChange={(e) => onChange('companyTaxNumber', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.companyTaxNumberPlaceholder || '300000000000003'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.companyPhone || 'رقم الهاتف'}
            </label>
            <input
              type="tel"
              value={companyPhone}
              onChange={(e) => onChange('companyPhone', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.companyPhonePlaceholder || '+966 50 000 0000'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.companyAddress || 'عنوان الشركة'}
            </label>
            <textarea
              value={companyAddress}
              onChange={(e) => onChange('companyAddress', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.companyAddressPlaceholder || 'أدخل عنوان الشركة الكامل'}
            />
          </div>
        </div>
        
        {/* شعار الشركة */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t?.companyLogo || 'شعار الشركة'}
          </label>
          <div className="flex items-center gap-4">
            {companyLogo && (
              <div className="w-20 h-20 border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <img 
                  src={companyLogo} 
                  alt="Company Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
              >
                <FaImage />
                {companyLogo ? (t?.changeLogo || 'تغيير الشعار') : (t?.uploadLogo || 'رفع شعار')}
              </label>
              {companyLogo && (
                <button
                  onClick={() => onChange('companyLogo', '')}
                  className="ml-2 px-3 py-2 text-red-600 hover:text-red-700 transition-colors"
                >
                  {t?.removeLogo || 'إزالة الشعار'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* معلومات الفاتورة */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <FaBuilding className="text-blue-600" />
          {t?.invoiceInfo || 'معلومات الفاتورة'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.invoiceNumber || 'رقم الفاتورة'}
            </label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => onChange('invoiceNumber', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={t?.invoiceNumberPlaceholder || 'INV-2024-001'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.currency || 'العملة'}
            </label>
            <select
              value={currency}
              onChange={(e) => onChange('currency', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="SAR">SAR - ريال سعودي</option>
              <option value="USD">USD - دولار أمريكي</option>
              <option value="EUR">EUR - يورو</option>
              <option value="GBP">GBP - جنيه إسترليني</option>
              <option value="AED">AED - درهم إماراتي</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.invoiceDate || 'تاريخ الفاتورة'}
            </label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => onChange('invoiceDate', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t?.dueDate || 'تاريخ الاستحقاق'}
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => onChange('dueDate', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BasicInfoTab; 